import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';

import {
    CreateBudgetRequestDocument,
    DeleteBudgetRequestsDocument,
    IBudgetRequestDetailFragment,
    IContentTypeValue,
    ICreateBudgetRequestInput,
    UpdateBudgetRequestDocument,
} from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { useApolloError } from 'common/hooks/useApolloError';
import { useExtraCategoryValues } from 'common/hooks/useExtraCategoryValues';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { usePermission } from 'user/hooks/usePermissions';
import { TBudgetRequestFormValues } from 'budget/types';
import { BudgetRequestForm } from 'budget/forms/BudgetRequestForm';

interface IProps {
    budgetRequest?: IBudgetRequestDetailFragment;
    userId: string;
    onClose?(): void;
    onCreate(budgetRequestId: string): void;
}

export const BudgetRequestFormController = ({
    budgetRequest,
    userId,
    onClose,
    onCreate,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const { hasPermission: canDelete } = usePermission({
        category: 'delete',
        contentType: IContentTypeValue.BudgetRequest,
        targetId: budgetRequest?.id,
    });

    const [createBudgetRequest] = useMutation(CreateBudgetRequestDocument);
    const [updateBudgetRequest] = useMutation(UpdateBudgetRequestDocument);
    const [deleteBudgetRequests] = useMutation(DeleteBudgetRequestsDocument);

    const {
        queryLoading: extraCategoriesLoading,
        handleUpdateExtraCategoryValues,
    } = useExtraCategoryValues({
        contentType: IContentTypeValue.BudgetRequest,
        modelId: budgetRequest?.id || '',
        typeNames: ['BudgetRequest'],
    });

    const getBudgetRequestInput = (
        values: Omit<TBudgetRequestFormValues, 'extraCategories'>
    ): ICreateBudgetRequestInput => ({
        title: values.title,
        amount: values.amount ?? 0,
        groupId: values.groupId,
        provider: values.provider || null,
        externalUrl: values.link ? `https://${values.link}` : null,
        description: values.description || null,
        startDate: values.startDate?.toISODate() ?? null,
        endDate: values.endDate?.toISODate() ?? null,
    });

    const handleCreateBudgetRequest: SubmitHandler<
        TBudgetRequestFormValues
    > = async (values) => {
        const { extraCategories: extraCategoryValues, ...rest } = values;

        let budgetRequestId: string | undefined;

        try {
            const result = await createBudgetRequest({
                variables: { budgetRequest: getBudgetRequestInput(rest) },

                refetchQueries: ['BudgetRequests', 'UserBudgetSummary'],
            });

            budgetRequestId =
                result.data?.createBudgetRequest?.budgetRequest.id;
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!budgetRequestId) return;

        const extraCategoriesSaved = await handleUpdateExtraCategoryValues(
            [],
            extraCategoryValues || {},
            budgetRequestId
        );

        if (!extraCategoriesSaved) return;

        displaySnackbar(translate('budgetRequestForm.submitSuccess'), {
            variant: 'success',
        });

        onCreate(budgetRequestId);

        return;
    };

    const handleUpdateBudgetRequest: SubmitHandler<
        TBudgetRequestFormValues
    > = async (values) => {
        if (!budgetRequest) return;

        const { extraCategories: extraCategoryValues, ...rest } = values;

        try {
            await updateBudgetRequest({
                variables: {
                    id: budgetRequest.id,
                    budgetRequest: getBudgetRequestInput(rest),
                },
                refetchQueries: ['BudgetRequests', 'UserBudgetSummary'],
            });

            const extraCategoriesSaved = await handleUpdateExtraCategoryValues(
                budgetRequest.extraCategoryValues,
                extraCategoryValues || {}
            );

            if (!extraCategoriesSaved) return;
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('budgetRequestForm.updateSuccess'), {
            variant: 'success',
        });

        onClose?.();

        return;
    };

    const handleDeleteBudgetRequest = async () => {
        if (!budgetRequest) return;

        try {
            const response = await deleteBudgetRequests({
                variables: { ids: [budgetRequest.id] },
                refetchQueries: ['BudgetRequests', 'UserBudgetSummary'],
            });

            const failedIds =
                response.data?.deleteBudgetRequests?.bulkDelete?.failedIds ??
                [];

            if (failedIds.length) {
                displaySnackbar(
                    translate('budgetRequestForm.deleteFailed', {
                        count: failedIds.length,
                    }),
                    { variant: 'error' }
                );

                return;
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('budgetRequestForm.deleteSuccess'), {
            variant: 'success',
        });

        onClose?.();

        return;
    };

    if (extraCategoriesLoading) return <Loader />;

    return (
        <BudgetRequestForm
            budgetRequest={budgetRequest}
            userId={userId}
            onClose={onClose}
            onCreate={handleCreateBudgetRequest}
            onDelete={canDelete ? handleDeleteBudgetRequest : undefined}
            onUpdate={handleUpdateBudgetRequest}
        />
    );
};
